import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getFirestore, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { DEFAULT_UNDERWRITING } from '../utils/profitCalculation';
import { resolveCoLivingKnobs } from '../utils/coLivingKnobs';
import { computeCoLivingFurnishing, conversionPremiumBeds, resolveAddedBaths } from '../utils/coLivingUnderwriting';
import { countEnsuiteBedrooms, resolveRateOverrides } from '../utils/coLivingBathSplit';
import { mergePlanRooms } from '../utils/imageLabelUtils';
import { cloudFunctionV2 } from '../functions';
import { useUnderwritingSettings } from './useUnderwritingSettings';
import { useUser } from '../contexts';
import { isUnderwritingPending } from '../lib/underwritingStatus';

// The system-default co-living knobs (DECIMALS), used when a caller doesn't pass a
// company override. Computed once — resolveCoLivingKnobs(null) is pure.
const DEFAULT_CO_LIVING_KNOBS = resolveCoLivingKnobs(null);

// Field projection sent to getLeadDetail. Excludes the big detail-irrelevant
// blobs that historically dragged the lead doc to ~80–100 KB:
//   • buyerMatches  (~1.6 MB on heavy leads — rendered on /leads/:id/buyers, not here)
//   • buyerMatchIds (~21 KB)
// Add a path here if a new column on the detail page renders something missing —
// forgetting is failsafe (the field renders empty until you add it).
// ⚠️ Failsafe, but silent: the footer CTA read lead.buyerMatches.totalMatched for its
// "Match to buyers" label and count badge, which this projection has never fetched, so
// it showed "Find buyers" on every lead — including one matched to 195 buyers. That is
// what `buyerMatchCount` below is for: a denormalized integer that gives the count
// without pulling either excluded blob.
const LEAD_DETAIL_FIELDS = [
    'leadId', 'propertyId', 'leadType', 'isArchived', 'archivedAt',
    'buyerMatchCount',
    'createdAt', 'updatedAt', 'completedAt',
    'address', 'latitude', 'longitude', 'countyId',
    'propertyType', 'propertyTypeVerification',
    // What the live listing text says about a teardown / sold-for-lot-value house (ingestion, LLM). The header
    // swaps bd/ba/sqft for the lot when isTeardown is true. 🪤 This list is a STRICT server-side projection, so
    // without this line the field is never fetched on first load and the header never switches.
    'teardownClaim',
    'verifiedCharacteristics', 'sourceSnapshot',
    // ingestedAskingPrice = the source-stated ask, written only by ingestion.
    // The header renders THIS (resolveIngestedAskingPrice); the other two are
    // the calculator's editable price and its first-edit snapshot.
    'currentAskingPrice', 'originalAskingPrice', 'ingestedAskingPrice',
    'estimates', 'computedSpread', 'computedMaoVsAsking',
    // FEMA flood zone (ReAPI propertyAttributes.floodZoneType, persisted to the lead
    // doc at ingestion by batchedFirestore). LeadDetailV2's header flood chip reads
    // `lead.floodZoneType` — and this list is a STRICT server-side projection
    // (getLeadDetail.js does `.select(...)` over exactly these paths), so without this
    // line the field is never fetched, the chip's `!= null` guard never passes, and it
    // renders on NO lead. That is how it shipped in #1637 and stayed dead: the chip was
    // added to the page without adding its source here.
    'floodZoneType',
    'leadReviewStatus', 'dealStatus',
    // Which buyers we sent this house to and what they said (functions/buyerFeedback/, derived),
    // and OUR contract on it. Absent ourContract = not under contract. Not rendered yet.
    'buyerOutreach', 'ourContract',
    'triage',
    'assigned', 'assignedTo', 'assignedUserIds',
    'imageUrls', 'imageFolders', 'imageDownloadStatus', 'photoUrls', 'repairCostImageUrls', 'imageSources', 'excludedImageUrls', 'excludedImageUrlReasons', 'photoOrder', 'featuredImageUrl', 'photoAvailability', 'videoUrls',
    'distressSignals',
    // motivationSignals / motivationTopSignals are the discrete seller-action flags
    // (new price cut / failed price cut / back on market / fell out of contract / listing
    // removed) — see functions/utils/sellerDistress.js deriveDistressFlags. motivationComponents
    // carries the same array plus the numbers behind it (ageDays, cut counts, state, ripeness).
    'motivationScore', 'motivationStrike', 'motivationTier', 'motivationSignals', 'motivationTopSignals',
    'motivationComponents',
    // When the price watch last re-scored this house. The header's market status and days-on-market read
    // motivationComponents (state, dom), which is only true AS OF this stamp — without it the page cannot
    // tell how many days the stored `dom` has aged. See utils/marketStatus.js.
    'distressSignalRefresh',
    'entries', 'currentSender', 'originalSender', 'senderMissing',
    // Classifier-detected sender company logo (a logo image found in the deal's
    // photos). resolveSenderLogo falls back to the sender-domain favicon.
    'senderLogoImageUrl',
    'customFields',
    // Foreclosure/trustee-sale fields (null for non-auction leads). Detail page
    // renders the Auction block from this. Whole scalar object is small.
    'auction',
    'automationId', 'portfolioId', 'portfolioName',
    // Source-channel + inbox-provider resolution — see useLeadsQuery.js for
    // the rationale. Detail page needs these to render the "Received at
    // <inbox>" sub-line under the Sent-by card and the channel-aware icon.
    'source', 'originSource', 'sourceMetadata',
    'ghlWriteBack', 'fubWriteBack', 'salesforceWriteBack',
    'sourceUrls', 'crmContactUrl', 'contactInfo',
    // What our scored calls, texts and emails say about this house: the agent's rapport and the seller's
    // stated position (functions/relationshipScoring/store.js#leadSummary). Small (a few axis values).
    // Rendered by the "Our relationship" row; absent ⇒ nothing scored, NOT a poor relationship.
    'relationship',
    'underwriting',
    'enrichment',  // page only reads enrichment.zillow.zpid; full subtree is small
    'propertyTaxes', 'insurance', 'hoaFees', 'propertyTaxSource', 'insuranceSource',
    'notes', 'description', 'listingStatus', 'closeOfEscrow', 'acquisitionNotes', 'acquisitionComments',
    // Creative-financing flags + structured deal terms (sub-to / seller finance).
    // Detail page renders the Financing Terms block from financingTerms.
    'sellerFinance', 'subTo', 'financingTerms',
    'dealUrls',
    'sourceFavicons',
    'status', 'syntheticPropertyId', 'addressUnverified',
    // In-flight underwriting stage — set to 'running_underwriting' while a manual
    // "Run analysis" pass runs, so the detail page can show the progress banner
    // even after a refresh mid-run.
    'processingStage',
    // Manual submissions (createLeadManual / MCP submit_deal) are kept even when
    // outside the buy box — these flag that so the detail page can badge it +
    // explain which criteria it missed (geometry-free snapshot in details).
    'outsideBuyBox', 'outsideBuyBoxReasons', 'outsideBuyBoxDetails',
    'processingSteps',
    'rawData',
    // Credit ledger — populated by chargeLeadCredit after the post-enrichment
    // gate. Lets support answer "did this lead charge, and for what?" without raw
    // Firestore. creditChargedFor is the per-analysis map { arv?, coLiving?, repair? }.
    'creditChargedFor', 'creditChargedAt', 'creditRefundedAt',
    'creditCharged', 'creditWaived', 'creditWaivedReason', 'creditWaivedAt'
];

// ── Shared query contract ────────────────────────────────────────────────────
// The key, the staleness window and the fetcher live here, not inline in the
// hook, so a prefetcher elsewhere (the Leads table primes this on row hover)
// writes to the SAME cache entry the page then reads. If these diverged the
// prefetch would populate a key nobody looks at and cost a round-trip for
// nothing — so there is exactly one definition of each.
export const LEAD_DETAIL_STALE_TIME = 30_000;
export const LEAD_DETAIL_GC_TIME = 5 * 60_000;

export function leadDetailQueryKey(companyId, propertyId) {
    return ['leadDetail', companyId, propertyId];
}

export async function fetchLeadDetail({ companyId, propertyId }) {
    const url = process.env.REACT_APP_GET_LEAD_DETAIL;
    const res = await cloudFunctionV2(url, {
        companyId,
        propertyId,
        leadFields: LEAD_DETAIL_FIELDS,
        // Only LeadDetailV2 needs the co-living-only verdict (drives the
        // calculator default). Opt in so the shared endpoint's other callers
        // (usePropertyData, Leads-list card images) skip the buyer-roster read.
        includeCoLivingOnly: true
    });
    if (res.status !== 200) {
        throw new Error(res.error || res.text || 'getLeadDetail failed');
    }
    // Return the full payload shape; consumer reads .lead / .property / .leadId.
    return {
        lead: res.lead || null,
        leadId: res.leadId || null,
        propertyData: res.property || {},
        // Whole active buyer roster is co-living-only — LeadDetailV2 uses this
        // to keep the calculator on the co-living view (never the flip ledger).
        coLivingOnly: res.coLivingOnly === true,
        notFound: res.lead == null
    };
}

// Warm the cache for a lead the user is about to open. A no-op when the entry
// is already fresh, so hovering back and forth across a row costs one fetch.
// Errors are swallowed: a failed prefetch must never surface — the page's own
// query will run on navigation and report the failure properly there.
export function prefetchLeadDetail(queryClient, { companyId, propertyId }) {
    if (!queryClient || !companyId || !propertyId) return;
    queryClient.prefetchQuery({
        queryKey: leadDetailQueryKey(companyId, propertyId),
        queryFn: () => fetchLeadDetail({ companyId, propertyId }),
        staleTime: LEAD_DETAIL_STALE_TIME,
        gcTime: LEAD_DETAIL_GC_TIME
    }).catch(() => { /* prefetch is best-effort */ });
}

// Loads a lead (by data.propertyId) plus its companion property doc and
// (lazily) the room-classifier labels for its photos. Returns the raw fetched
// data alongside `calcInitialValues` — a one-time snapshot of the values the
// calculator inputs should hydrate from. The page owns the actual input state;
// the hook just packages everything it needs to seed those inputs.
//
// Backed by TanStack Query: subsequent visits to the same lead within 30s
// hit cache (instant back-nav). Optimistic edits via setLead update the
// cache so navigating away and back shows the edited state, not stale.
export function useLeadFetch({ companyId, companyLoading, propertyId, coLivingKnobs = null }) {
    const queryClient = useQueryClient();
    const { userData } = useUser();
    const isAdmin = !!userData?.isAdmin;

    const queryKey = useMemo(() => leadDetailQueryKey(companyId, propertyId), [companyId, propertyId]);

    // The main fetch: lead + property in one round-trip via getLeadDetail.
    // Key/fetcher/staleness come from the shared contract above so a hover
    // prefetch from the Leads table lands on this exact entry.
    const leadQuery = useQuery({
        queryKey,
        enabled: !companyLoading && !!companyId && !!propertyId,
        staleTime: LEAD_DETAIL_STALE_TIME,
        gcTime: LEAD_DETAIL_GC_TIME,
        queryFn: () => fetchLeadDetail({ companyId, propertyId })
    });

    // Event-driven completion watcher (replaces polling). While this lead is
    // still being processed by propertyIngestionV2 (status queued/processing),
    // subscribe to its Firestore doc; the moment status leaves pending,
    // invalidate so getLeadDetail refetches once (re-pulling the lead + its
    // property doc, where ARV/repair analysis also lands). The listener is
    // idle until something actually changes — no timer, no repeated calls.
    //
    // Gated on isUnderwritingPending, not on the raw status: a lead stranded at
    // 'processing' by the resubmission merge path (which never writes a terminal
    // status) would otherwise hold a live Firestore listener for the whole session
    // waiting on a transition that can never come.
    const watchedLead = leadQuery.data?.lead;
    const watchedStatus = watchedLead?.status;
    const watchedPending = isUnderwritingPending(watchedLead);
    const watchedLeadId = leadQuery.data?.leadId;
    useEffect(() => {
        if (!companyId || !watchedLeadId || !watchedPending) return undefined;
        const db = getFirestore();
        const ref = doc(db, 'companies', companyId, 'leads', watchedLeadId);
        // Act on any terminal status (incl. the very first snapshot, in case
        // the lead finished between the page fetch and this listener attaching).
        // A still-pending echo is a no-op; invalidate→refetch flips watchedStatus
        // out of pending and tears this listener down, so there's no loop.
        const unsub = onSnapshot(ref, (snap) => {
            const s = snap.exists() ? snap.data()?.data?.status : null;
            if (!snap.exists() || (s && s !== 'queued' && s !== 'processing')) {
                queryClient.invalidateQueries({ queryKey });
            }
        }, (err) => console.warn('[useLeadFetch] status watcher error', err));
        return () => unsub();
    }, [companyId, watchedLeadId, watchedPending, watchedStatus, queryClient, queryKey]);

    // Company-level underwriting settings — its own query so opening multiple
    // leads in a session shares one cached result.
    const settingsQuery = useUnderwritingSettings(companyId);

    // Image labels are fetched lazily as a side-effect after the main data
    // lands. Kept as local state because the source can be either the
    // property doc's mirror (free) or a fallback Firestore read.
    const [imageLabels, setImageLabels] = useState(null);

    // Mirrors leadDocId in a ref so async callbacks anywhere in the page tree
    // can detect mid-flight nav and skip optimistic state writes that would
    // clobber the newly-loaded lead.
    const currentLeadDocIdRef = useRef(null);
    useEffect(() => { currentLeadDocIdRef.current = leadQuery.data?.leadId || null; }, [leadQuery.data?.leadId]);

    // ── Session-local calculator-input ledger ─────────────────────────────────
    // Every calculator field the page has persisted this session, keyed by field name.
    // The page writes those straight to Firestore (LeadDetailV2's persistCalcField) and
    // NOTHING pushes the result back into this query's cache, so
    // `propertyData.calculatorInputs` stays frozen at whatever the page first loaded.
    // Layer this ledger on top of it and the operator's own values always win, so any
    // later recompute of calcInitialValues — a refetch, a refreshProperty, a setLead —
    // re-derives from what they actually typed instead of resurrecting the pre-edit doc.
    //
    // Keyed BY propertyId, not scoped to the current one. This page does not remount
    // between leads, so a single scope would both leak lead A's edits into lead B and
    // lose them on the way back — and back is the cheap direction: within staleTime the
    // return trip is served entirely from cache, i.e. from the pre-edit doc.
    const calcOverridesRef = useRef({});
    const applyCalcOverride = useCallback((field, value) => {
        if (!propertyId) return;
        const all = calcOverridesRef.current;
        calcOverridesRef.current = {
            ...all,
            [propertyId]: { ...(all[propertyId] || {}), [field]: value },
        };
    }, [propertyId]);

    // Image-labels effect: derive from the property doc's mirror when present,
    // otherwise fall back to property_images/{zpid} — but only for admins,
    // since Firestore rules deny that read for normal users (it 403s silently
    // on every old lead today). Old leads classified before the mirror existed
    // just get no labels; the gallery degrades gracefully.
    useEffect(() => {
        const lead = leadQuery.data?.lead;
        const propertyData = leadQuery.data?.propertyData;
        if (!lead || !propertyData) {
            setImageLabels(null);
            return;
        }
        const mirroredLabels = propertyData.subjectImageRoomLabels;
        if (mirroredLabels?.labels?.length) {
            setImageLabels({
                // photoPlanRooms is the floor plan's name for the room each photo was
                // shot in (co-living room locator). It lives in its own field because
                // the labels array is rebuilt on every classify — fold it on here so
                // the gallery sees the same shape the public deal projections build.
                labels: mergePlanRooms(mirroredLabels.labels, propertyData.photoPlanRooms),
                aduPass: !!mirroredLabels.aduPass,
            });
            return;
        }
        if (!isAdmin) {
            setImageLabels(null);
            return;
        }
        let cancelled = false;
        const imagesKey = lead?.enrichment?.zillow?.zpid || propertyId;
        if (!imagesKey) return;
        const db = getFirestore();
        getDoc(doc(db, 'property_images', String(imagesKey))).then(snap => {
            if (cancelled || !snap.exists()) return;
            const classifierV1 = snap.data()?.classifierV1;
            if (!classifierV1?.storageUrls?.length) return;
            const labels = classifierV1.storageUrls.map((url, i) => ({
                firebaseUrl: url,
                mlsPhotoUrl: null,
                roomType: classifierV1.classifications?.[i]?.room_type || null,
            }));
            setImageLabels({ labels, aduPass: false });
        }).catch(err => {
            if (err?.code === 'permission-denied') return;
            console.warn('Failed to load room labels for', imagesKey, err);
        });
        return () => { cancelled = true; };
    }, [leadQuery.data?.lead, leadQuery.data?.propertyData, isAdmin, propertyId]);

    // Calculator-input hydration values. Computed once per fresh load (i.e.
    // once per (companyId, propertyId, settings) tuple). Memoized on the
    // raw query data identities, not on every re-render of the consumer.
    const calcInitialValues = useMemo(() => {
        const lead = leadQuery.data?.lead;
        const propertyData = leadQuery.data?.propertyData;
        if (!lead || !propertyData) return null;
        // Hold off until the company settings have resolved. The page seeds its inputs
        // from this ONCE per lead now, so producing a value while `company` is still {}
        // would pin every company-defaulted field (target profit, hold months, the rate
        // knobs) to the system baseline for the rest of the session. The page renders its
        // loading state throughout this window — `loading` below already covers it.
        if (settingsQuery.isLoading) return null;
        const company = settingsQuery.data?.data || {};
        // Session-local edits layered over the cached doc — see calcOverridesRef. Read
        // through the ref and deliberately NOT as a dependency: recording an edit must
        // not itself recompute this object, or the page would re-seed the calculator on
        // every persisted keystroke, which is the clobbering the ledger exists to stop.
        // The merge lands on the next genuine recompute, which is when it's needed.
        const localCalc = (propertyId && calcOverridesRef.current[propertyId]) || {};
        const calc = { ...(propertyData.calculatorInputs || {}), ...localCalc };
        const companyTargetProfitPct = company.targetProfitPercentage != null
            ? Math.round(company.targetProfitPercentage * 100)
            : null;
        const nextArv = calc.arv ?? propertyData.arvData?.arv ?? lead.underwriting?.arv ?? null;
        const nextRepair = calc.repairCost ?? propertyData.repairCost?.totalRepairCost ?? lead.underwriting?.repairCost ?? null;
        const nextTargetProfitPct = calc.targetProfitPercent ?? companyTargetProfitPct ?? null;
        const nextSellerAskingPrice = calc.sellerAskingPrice ?? null;

        // Storage convention: rate fields on calculatorInputs are stored as
        // percent numbers (12 for 12%). solveMao + DEFAULT_UNDERWRITING expect
        // decimals (0.12). Divide by 100 at hydration. Money fields + holdMonths
        // are stored as-is. Cascade per field: per-property → company → DEFAULT.
        const safeNum = (v, fallback) => (typeof v === 'number' && !isNaN(v) ? v : fallback);
        const safeRate = (v, fallbackDecimal) =>
            (typeof v === 'number' && !isNaN(v) ? v / 100 : fallbackDecimal);
        const companyRate = (v, defaultDecimal) =>
            (typeof v === 'number' && !isNaN(v) ? v : defaultDecimal);
        // Taxes and insurance are the co-living model's only fixed costs, and neither is
        // ever legitimately $0, so a missing figure must not read as one — a silent zero
        // overstates NOI and, through the reverse cap-rate solve, the max offer. Carry
        // "do we actually know" flags alongside the safe-zeros the math needs. HOA needs
        // no flag: $0 is its common, correct value.
        const rawPropertyTaxes = calc.propertyTaxes ?? propertyData.propertyTaxes ?? lead.propertyTaxes;
        const rawInsurance = calc.insurance ?? propertyData.insurance ?? lead.insurance;
        const isNum = (v) => typeof v === 'number' && !isNaN(v);
        const nextUnderwriting = {
            propertyTaxes: safeNum(rawPropertyTaxes, 0),
            insurance: safeNum(rawInsurance, 0),
            hoaFees: safeNum(calc.hoaFees ?? propertyData.hoaFees ?? lead.hoaFees, 0),
            taxesKnown: isNum(rawPropertyTaxes),
            insuranceKnown: isNum(rawInsurance),
            // Which supplier (zillow/realtor/reapi) won the corroboration in
            // processor.js — a calculator-edited figure (calc.*) is the user's own
            // number, not any supplier's, so it carries no source.
            propertyTaxSource: calc.propertyTaxes == null ? (propertyData.propertyTaxSource ?? lead.propertyTaxSource ?? null) : null,
            insuranceSource: calc.insurance == null ? (propertyData.insuranceSource ?? lead.insuranceSource ?? null) : null,
            // No supplier quotes a premium; the backend derives insurance as the
            // conservative max of two payment-calculator estimates and a 0.5%-of-value
            // floor. A user-entered override on calculatorInputs is NOT an estimate.
            insuranceIsEstimated: calc.insurance == null && propertyData.insuranceIsEstimated === true,
            // Same per-property → company → DEFAULT cascade as the rows below, with the
            // co-living knob as the company tier. ONE fee per lead: this field already fed
            // the co-living calculator as `assignmentFee` long before it had a box there, so
            // giving co-living a second, separate fee would let one page quote two different
            // offers on the same house. The knob is 0 for every company that hasn't set one,
            // so nothing moves anywhere until a company does.
            wholesaleFee: safeNum(calc.wholesaleFee, safeNum((coLivingKnobs || DEFAULT_CO_LIVING_KNOBS).wholesaleFee, 0)),
            holdMonths: safeNum(calc.holdMonths, safeNum(company.holdMonths, DEFAULT_UNDERWRITING.holdMonths)),
            annualInterestRate: safeRate(calc.annualInterestRate, companyRate(company.annualInterestRate, DEFAULT_UNDERWRITING.annualInterestRate)),
            commissionRate: safeRate(calc.commissionRate, companyRate(company.commissionRate, DEFAULT_UNDERWRITING.commissionRate)),
            titleRate: safeRate(calc.titleRate, companyRate(company.titleRate, DEFAULT_UNDERWRITING.titleRate)),
            pointsRate: safeRate(calc.pointsRate, companyRate(company.pointsRate, DEFAULT_UNDERWRITING.pointsRate)),
            downPayment: safeRate(calc.downPayment, companyRate(company.downPayment, DEFAULT_UNDERWRITING.downPayment)),
        };

        // Co-living calculator seed. Income side comes from the co-living pipeline
        // (grossRent.ratePerRoom + the projected room count); the rest are the
        // high-leverage knobs a user tweaks per-lead. Cap rate + down payment are
        // stored as PERCENT numbers on calculatorInputs (7, 20), same convention as
        // targetProfitPercent. Fine-grained opex knobs default from code
        // (DEFAULT_CO_LIVING_UNDERWRITING) and aren't per-lead editable in v1.
        const interior = propertyData.coLivingInterior || {};
        // The room count is every genuinely-possible bedroom (furnish-only AND
        // build-out adds) — ceilingWithConstruction, already bath-capped. Read the RAW
        // projection field first, NOT the cached grossRent.beds: deals underwritten
        // before this change carry a stale grossRent.beds (the old clean-only ceiling),
        // so preferring it would keep showing the low count until every deal is
        // reprocessed. ceilingWithConstruction is always current in the stored doc.
        // With no legible floor plan, projectedBedrooms is null and the count lives in
        // roughBedroomEstimate.total (existing + adds) — the same number the badge +
        // conversion banner show — so a no-floor-plan deal seeds the calculator (and
        // its gross rent / max offer) instead of blanking.
        const derivedRooms = interior.projectedBedrooms?.ceilingWithConstruction
            ?? interior.grossRent?.beds
            ?? interior.projectedBedrooms?.ceiling
            ?? interior.roughBedroomEstimate?.total
            ?? null;
        // Rate falls back to the standalone market room rate (persisted even when
        // there's no legible floor plan to count beds), so a needs-review deal still
        // pre-fills the market $/room even though the room count is blank.
        //
        // IMPORTANT: when the pipeline's private/shared split fired, grossRent.ratePerRoom
        // is a BLENDED average — not a rate any room actually rents for. The real
        // shared baseline is bathSplit.sharedRate. Seeding the shared-rate box from the
        // blend would show the investor a number that matches nothing on PadSplit, and
        // would re-blend an already-blended rate.
        const derivedSharedRate = interior.grossRent?.bathSplit?.sharedRate
            ?? interior.grossRent?.ratePerRoom
            ?? interior.roomRate?.ratePerRoom
            ?? null;
        // The private median rides along in rateMeta whether or not the split fired, so a
        // house whose premium the pipeline suppressed (no bath surplus at ITS bed count)
        // can still earn it once the analyst dials the plan down.
        const derivedPrivateRate = interior.grossRent?.bathSplit?.privateRate
            ?? interior.grossRent?.privateRate
            ?? interior.roomRate?.privateRate
            ?? null;
        // Kept for the legacy single-rate seed + the reset affordance.
        const derivedRate = interior.grossRent?.ratePerRoom ?? interior.roomRate?.ratePerRoom ?? null;
        // Distinct bedrooms with their OWN full bath. Zero ⇒ the calculator never grows
        // a second rate field; this house has nothing to split.
        const ensuiteCount = countEnsuiteBedrooms(interior.bathAccess);
        const seedRooms = safeNum(calc.coLivingRooms, derivedRooms);
        // conversionPremium (functions/coLiving/conversionPremium.js) is the real,
        // Cost of converting this specific house — furnishing + egress windows +
        // basement/garage/build-out finish, as a {low, high} range. Computed during
        // ingestion and persisted on coLivingInterior.conversionPremium. Struck from
        // the measured floor plan when one is legible (basis 'ceilingWithConstruction')
        // or from the photo-typed rough estimate when not (basis 'roughEstimate' —
        // wider band, lower confidence; same cost model). Defaults the calculator's
        // editable line to `high` — conservative, per this app's lean-conservative
        // convention for investor-facing numbers — with the flat knob formula as the
        // legacy fallback for docs underwritten before rough-route premiums existed.
        const conversionPremium = interior.conversionPremium || null;
        // Company-specific underwriting assumptions (DECIMALS) drive the co-living input
        // DEFAULTS — cap rate, down payment, mortgage rate, and the no-floor-plan
        // furnishing fallback — so the calculator opens on the company's own numbers, not
        // the system baseline. A saved per-lead value (calc.co*) still wins over these.
        const clKnobs = coLivingKnobs || DEFAULT_CO_LIVING_KNOBS;
        // Beds ADDED beyond existing (drives the per-added-bedroom knob on no-floor-plan
        // deals); baths ADDED is a saved per-lead human input (⇒ 0). Same inputs the
        // matcher uses, so the seed default equals the matched conversion cost.
        const existingBeds = Number.isFinite(interior.projectedBedrooms?.existing)
            ? interior.projectedBedrooms.existing
            : (Number.isFinite(interior.roughBedroomEstimate?.existing)
                ? interior.roughBedroomEstimate.existing
                : (Number.isFinite(propertyData.bedrooms) ? propertyData.bedrooms : 0));
        const addedBeds = Math.max(0, (seedRooms || 0) - existingBeds);
        // ⚠️ NOT `calc.coLivingBathsAdded` alone. The backend charges the ESTIMATE
        // (`coLivingInterior.bathAddition`) when nobody typed a figure, so reading only the
        // typed field shows $0 for a bathroom the strategy-fit gate charged $10k for.
        // Mirrors functions/utils/coLivingReturns.js#resolveAddedBaths.
        const addedBaths = resolveAddedBaths(interior, calc);
        // Shared with the deal-page calculator + matcher: the premium's MIDPOINT on
        // floor-plan deals, else furnish every bedroom + per-added-bed/bath knobs. All
        // new knobs default 0 ⇒ this reduces to today's rooms × furnishingPerRoom.
        const derivedFurnishingCost = (conversionPremium || seedRooms != null)
            ? computeCoLivingFurnishing({
                conversionPremium: conversionPremium ?? null,
                premiumBeds: conversionPremiumBeds(interior),
                totalBeds: seedRooms,
                addedBeds,
                addedBaths,
                knobs: clKnobs,
            })
            : null;
        // Rate-override precedence, shared with the deal page + the backend headline
        // resolver: the legacy flat coLivingRatePerRoom means "every room at this rate",
        // so it seeds BOTH boxes (collapsing the premium — which is what it always
        // meant). A per-group override supersedes it.
        const { flat, sharedRateOverride, privateRateOverride } = resolveRateOverrides(calc);

        const nextCoLiving = {
            rooms: seedRooms,
            ratePerRoom: safeNum(calc.coLivingRatePerRoom, derivedRate),
            // The legacy flat rate AS STORED — null when the lead never had one. Distinct
            // from `ratePerRoom` above, which falls back to the pipeline rate and so is
            // non-null on almost every lead. The calculator gates its "retire the flat
            // rate" migration on this: gate on the fallback instead and every lead looks
            // like a legacy one, pinning both room rates as overrides on the first edit
            // (and freezing them against the next PadSplit repricing).
            ratePerRoomOverride: safeNum(calc.coLivingRatePerRoom, null),
            sharedRatePerRoom: sharedRateOverride ?? derivedSharedRate,
            // A flat override deliberately drags the private rate down to it, so no
            // premium survives an explicit "rooms rent for $X".
            privateRatePerRoom: flat ?? privateRateOverride ?? derivedPrivateRate,
            ensuiteCount,
            derivedSharedRate,
            derivedPrivateRate,
            // Preserve 2 decimals of the company knob (7.5% cap must not seed as 8%) so
            // the calculator agrees with the matcher + buyer deal page, which use the
            // exact value. Whole-number defaults (7, 20) are unchanged by the rounding.
            targetCapRatePercent: safeNum(calc.coLivingTargetCapRate, Math.round(clKnobs.targetCapRate * 10000) / 100),
            downPaymentPercent: safeNum(calc.coLivingDownPayment, Math.round(clKnobs.downPayment * 10000) / 100),
            mortgageRatePercent: safeNum(calc.coLivingMortgageRate, Math.round(clKnobs.mortgageRate * 10000) / 100),
            furnishingCost: safeNum(calc.coLivingConversionCost, derivedFurnishingCost),
            // The conversion cost AS STORED — null when the analyst never overrode it.
            // Distinct from `furnishingCost` above, which falls back to the DERIVED cost and
            // so is non-null on almost every lead. The calculator must gate on this one: use
            // the merged value as the override and the derived cost is dead on arrival — the
            // box is seeded with it at load, so it always looks like an override, and the
            // ledger would keep pricing the plan the lead was underwritten at no matter how
            // the analyst edits the rooms. Same distinction as `ratePerRoomOverride`.
            furnishingCostOverride: safeNum(calc.coLivingConversionCost, null),
            // The operator's own rehab figure for THIS house — null when they never typed
            // one, in which case the ledger prices off the rent-ready assessment (or the
            // company's turnkey buffer) and the box merely DISPLAYS that. Same
            // override-vs-derived distinction as furnishingCostOverride above: seed the
            // calculator's override state from this, never from the displayed value, or a
            // drive-by focus freezes today's estimate onto the lead.
            repairCostOverride: safeNum(calc.coLivingRepairCost, null),
            // The operator's own pool-fill figure — null when they never typed one, in which
            // case the line prices off the company knob on a confirmed pool. Same
            // override-vs-derived rule as the two above; a stored 0 is a real answer ("keeping
            // the pool") and must survive as 0, which safeNum's null-only fallback preserves.
            poolFillOverride: safeNum(calc.coLivingPoolFillCost, null),
            // ⚠️ Seeds the deal-page box, so it must agree with the money. `resolveAddedBaths`
            // falls back to the ESTIMATE (`coLivingInterior.bathAddition`) when nobody typed a
            // figure — otherwise the box reads 0 while the ledger charges for a bathroom.
            bathsAdded: resolveAddedBaths(interior, calc),
            derivedRooms,
            derivedRate,
            derivedFurnishingCost,
            conversionPremium,
            bathCeiling: interior.projectedBedrooms?.bathCeiling ?? null,
            // Per-card bedroom edits (the conversion cards' 0…N steppers) and the
            // existing-bed base they sit on. The map remembers which cards were
            // dialled; its derived sum is what lives in `rooms`/coLivingRooms.
            spotBeds: (calc.coLivingSpotBeds && typeof calc.coLivingSpotBeds === 'object')
                ? calc.coLivingSpotBeds
                : {},
            // Per-bath declarations ({ bathRoomId: 'private'|'shared' }) — the bathroom
            // cards' share/keep-private toggles. Empty ⇒ every bath sits on the company's
            // ensuite policy, which is what the backend projection was struck under.
            bathPlan: (calc.coLivingBathPlan && typeof calc.coLivingBathPlan === 'object')
                ? calc.coLivingBathPlan
                : {},
            // Per-line parking edits ({ lineKey: spaceCount }) — the parking card's
            // steppers. Re-derives the parking verdict; no companion money field.
            parkingSpaces: (calc.coLivingParkingSpaces && typeof calc.coLivingParkingSpaces === 'object')
                ? calc.coLivingParkingSpaces
                : {},
            // Derived effective-total cache for lean buyer surfaces (deal-feed card).
            parkingTotalSpaces: typeof calc.coLivingParkingTotalSpaces === 'number'
                ? calc.coLivingParkingTotalSpaces
                : null,
            existingBeds,

            // ── Financing mode (src/utils/coLivingFinancingModes.js) ────────────────────
            // Which structure this deal is priced under. null ⇒ the calculator opens on the
            // condition default, so the back catalogue keeps rendering exactly as it does now.
            financingMode: typeof calc.coLivingFinancingMode === 'string' ? calc.coLivingFinancingMode : null,
            // The after-repair value the refinance is sized against. ONLY the raw override is
            // shipped: the merged value depends on propertyData (rentReadyData / arvData) and
            // is resolved by resolveStabilizedValue at render, where the whole precedence chain
            // is in one place. Same override-vs-derived rule as furnishingCostOverride above.
            stabilizedValueOverride: safeNum(calc.coLivingStabilizedValue, null),
            // Per-lead bridge terms. Merged with the company knob for DISPLAY (the box opens on
            // what is actually being used), and shipped raw alongside so the calculator can tell
            // an operator's 11% from the company's 11% and offer Reset only on the former.
            bridgeRatePercent: safeNum(calc.coLivingBridgeRate, Math.round(clKnobs.bridgeRate * 10000) / 100),
            bridgeRatePercentOverride: safeNum(calc.coLivingBridgeRate, null),
            bridgeMonths: safeNum(calc.coLivingBridgeMonths, clKnobs.bridgeMonths),
            bridgeMonthsOverride: safeNum(calc.coLivingBridgeMonths, null),
            refinanceLTVPercent: safeNum(calc.coLivingRefinanceLTV, Math.round(clKnobs.refinanceLTV * 10000) / 100),
            refinanceLTVPercentOverride: safeNum(calc.coLivingRefinanceLTV, null),
            // Your own agent's commission on THIS purchase. Per-lead value first, else the
            // company knob (0 for anyone who hasn't set one), so the box opens on the operator's
            // normal case and only differs where they typed something for this house.
            buyerAgentPercent: safeNum(calc.coLivingBuyerAgentPct, Math.round(clKnobs.buyerAgentPct * 10000) / 100),
            refinanceRatePercent: safeNum(calc.coLivingRefinanceRate, Math.round(clKnobs.refinanceRate * 10000) / 100),
            refinanceRatePercentOverride: safeNum(calc.coLivingRefinanceRate, null),
            // How much of their own money the buyer will accept leaving in after the refinance.
            // 0 ("leave nothing in") is the meaningful default, not a missing value.
            targetCashLeft: safeNum(calc.coLivingTargetCashLeft, 0),
            // Seller-finance / subject-to / hybrid (the `creative` mode). Unlike the bridge terms
            // above, the fallback isn't a COMPANY knob — there is no company-wide seller-finance
            // rate — it's the backend's own per-lead solve (offerRecommendation.creative,
            // computed once at ingestion/reprice by coLivingOfferProducer.js). A saved per-lead
            // override still wins over that solve, same precedence as every other financing field.
            creativeDownPctPercent: safeNum(calc.coLivingCreativeDownPct, safeNum(propertyData.offerRecommendation?.creative?.downPct, 5)),
            creativeDownPctPercentOverride: safeNum(calc.coLivingCreativeDownPct, null),
            creativeCarryRatePercent: safeNum(calc.coLivingCreativeCarryRate, safeNum(propertyData.offerRecommendation?.creative?.rate, null)),
            creativeCarryRatePercentOverride: safeNum(calc.coLivingCreativeCarryRate, null),
            // The seller's existing loan payment, as the backend resolved it — overridable when
            // the operator knows the real figure differs. Same merged/override split as the two
            // above; the box shows the merged value, the Override tracks whether Reset should show.
            creativeExistingPaymentDollars: safeNum(calc.coLivingCreativeExistingPayment, safeNum(propertyData.offerRecommendation?.creative?.underlyingPay, null)),
            creativeExistingPaymentDollarsOverride: safeNum(calc.coLivingCreativeExistingPayment, null),
            // The seller's listing-agent commission — no backend solve to fall back to (the
            // recommender never modelled one) and no company knob (per-lead, not company-wide).
            // 0 unless the operator has typed one for this deal.
            creativeSellerCommissionPercent: safeNum(calc.coLivingCreativeSellerCommission, 0),
        };

        // Long-term-rental calculator seed. Only the two per-lead overrides live here;
        // everything else (the RentCast rent estimate + the company knobs) the deal page
        // reads straight off propertyData / companyData. null ⇒ no override, so the ledger
        // falls back to the RentCast AVM rent and the AI rent-ready repair line.
        const nextRental = {
            rentMonthly: typeof calc.rentMonthly === 'number' ? calc.rentMonthly : null,
            rentalRehabCost: typeof calc.rentalRehabCost === 'number' ? calc.rentalRehabCost : null,
            // BRRRR refinance override — null ⇒ resolveBrrrrInputs falls back to the
            // arvAgent rent-ready tier, then full ARV.
            rentReadyValue: typeof calc.rentReadyValue === 'number' ? calc.rentReadyValue : null,
        };

        return {
            arv: nextArv,
            repairCost: nextRepair,
            // Per-system on/off for the big-ticket repair lines (roof / HVAC / water heater /
            // panel / plumbing). ONLY the systems the operator explicitly switched — an absent
            // key means "whatever the model decided", so an untouched lead behaves exactly as
            // it did before the switches existed, and a re-score that changes the model's mind
            // is still free to. The MONEY those switches move already lives in
            // `calc.repairCost`; this map is what re-renders the switches in the right
            // position on the next load.
            bigTicketOverrides: (calc.bigTicketOverrides && typeof calc.bigTicketOverrides === 'object')
                ? calc.bigTicketOverrides
                : {},
            // What the operator learned about roof / HVAC / water heater / panel on a call
            // with the listing agent — `{ [system]: 'recent' | 'due' | null }`. An absent or
            // null entry means "use the model", so an untouched lead scores exactly as it did
            // before this existed.
            //
            // 🔴 These are COUNTS, never dollars. A verdict changes how many of the big four
            // are assumed due, which moves the TIER; the tier's table price is all-in. The
            // resulting money is already in `calc.repairCost` (and the tier in
            // `calc.repairTier`) — this map only re-renders the toggles in the right position.
            systemsVerified: (calc.systemsVerified && typeof calc.systemsVerified === 'object')
                ? calc.systemsVerified
                : {},
            // The operator's tier pick, authoritative over the dollar cache beside it: a tier
            // survives the company re-rating its table, a stored number silently does not.
            repairTier: typeof calc.repairTier === 'string' ? calc.repairTier : null,
            targetProfitPercent: nextTargetProfitPct,
            sellerAskingPrice: nextSellerAskingPrice,
            underwriting: nextUnderwriting,
            coLiving: nextCoLiving,
            rental: nextRental,
            askingPrice: lead.currentAskingPrice ?? null,
            initialAskingPrice: lead.originalAskingPrice
                ?? lead.entries?.[0]?.askingPrice
                ?? lead.currentAskingPrice
                ?? null,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leadQuery.data?.lead, leadQuery.data?.propertyData, settingsQuery.data?.data, settingsQuery.isLoading, coLivingKnobs, propertyId]);

    // Optimistic-update setter. Updates the TanStack cache directly so that
    // navigating away and back shows the edited state, not the stale server
    // version. Mirrors React's useState setter signature (value or updater fn).
    const setLead = useCallback((updater) => {
        queryClient.setQueryData(queryKey, (old) => {
            if (!old) return old;
            const next = typeof updater === 'function' ? updater(old.lead) : updater;
            return { ...old, lead: next };
        });
    }, [queryClient, queryKey]);

    // Re-fetch just the property doc + its subject-room-labels mirror. Used by
    // the image-download listener after post-hoc re-analysis finishes — the
    // property doc has fresh repairCost.analysis and subjectImageRoomLabels and
    // we want the open page to pick them up without a hard refresh.
    const refreshProperty = useCallback(async () => {
        if (!companyId || !propertyId) return;
        try {
            const db = getFirestore();
            const propertyRef = doc(db, 'companies', companyId, 'properties', propertyId);
            const snap = await getDoc(propertyRef);
            if (!snap.exists()) return;
            const fresh = snap.data()?.data || {};
            // Replacing propertyData is itself the signal the page re-seeds on — see the
            // hydration effect in LeadDetailV2. Nothing extra to publish here.
            queryClient.setQueryData(queryKey, (old) => old ? { ...old, propertyData: fresh } : old);
            const mirrored = fresh.subjectImageRoomLabels;
            if (mirrored?.labels?.length) {
                setImageLabels({
                    labels: mergePlanRooms(mirrored.labels, fresh.photoPlanRooms),
                    aduPass: !!mirrored.aduPass,
                });
            }
        } catch (err) {
            console.warn('[useLeadFetch] refreshProperty failed', err);
        }
    }, [companyId, propertyId, queryClient, queryKey]);

    // `loading` stays true while the query is enabled but hasn't produced a
    // definitive answer yet. Covers the brief render gap where `enabled` flips
    // true but TanStack Query hasn't yet set fetchStatus='fetching' — during
    // that gap isLoading=false and data=undefined, which used to let the page
    // flash the not-found card as a placeholder. When the query is permanently
    // disabled (e.g. no companyId for an unbootstrapped user), this stays
    // false so the page can fall through to its terminal not-found UI rather
    // than spinning forever.
    const queryEnabled = !companyLoading && !!companyId && !!propertyId;
    const hasResolvedLead = leadQuery.data !== undefined || !!leadQuery.error;
    return {
        lead: leadQuery.data?.lead || null,
        setLead,
        leadDocId: leadQuery.data?.leadId || null,
        propertyData: leadQuery.data?.propertyData || null,
        coLivingOnly: leadQuery.data?.coLivingOnly === true,
        imageLabels,
        loading:
            leadQuery.isLoading
            || settingsQuery.isLoading
            || (leadQuery.isFetching && !leadQuery.data)
            || (queryEnabled && !hasResolvedLead),
        notFound: leadQuery.data?.notFound === true || (!!leadQuery.error && !leadQuery.data),
        calcInitialValues,
        // Records a calculator-input value the page has just persisted, so the cached
        // property doc can never re-seed a stale one over it.
        applyCalcOverride,
        currentLeadDocIdRef,
        refreshProperty,
    };
}
